import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TexHubLogo from '@/components/brand/TexHubLogo';
import LaunchPromoBanner from '@/components/LaunchPromoBanner';
import RegionSelector from '@/components/RegionSelector';
import { Mail, Lock, User, Loader2, CheckCircle2, ArrowRight, Gift, Building2, Phone, MapPin, ChevronRight, ChevronLeft } from 'lucide-react';

// Mexican states
const MEXICAN_STATES = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas",
  "Chihuahua", "Ciudad de México", "Coahuila", "Colima", "Durango", "Estado de México",
  "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "Michoacán", "Morelos", "Nayarit",
  "Nuevo León", "Oaxaca", "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí",
  "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"
];

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loginWithGoogle } = useAuth();
  const { isUSA } = useLocale();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = user info, 2 = company info
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  
  const [formData, setFormData] = useState({
    // User data
    name: '',
    email: '',
    password: '',
    // Company data
    company_name: '',
    phone: '',
    location_state: '',
    location_city: '',
    // Categories
    is_proveedor: false,
    is_maquilero: false,
    is_comprador: false,
    // Proveedor subcategories
    proveedor_telas: false,
    proveedor_avios: false,
    proveedor_etiquetas: false,
    proveedor_hilos: false,
    proveedor_empaque: false,
    proveedor_otros: false,
    // Maquilador subcategories
    maquilero_corte: false,
    maquilero_confeccion: false,
    maquilero_acabados: false,
    maquilero_bordado: false,
    maquilero_estampado: false,
    maquilero_tenido: false,
    maquilero_otros: false,
    // Comprador subcategories
    comprador_fabricante: false,
    comprador_revendedor: false,
    comprador_tienda: false,
    comprador_marca: false,
    comprador_exportador: false,
    comprador_otros: false,
  });

  // Check for referral code in URL or localStorage
  useEffect(() => {
    const refFromUrl = searchParams.get('ref');
    const refFromStorage = localStorage.getItem('texhub_referral_code');
    
    if (refFromUrl) {
      setReferralCode(refFromUrl.toUpperCase());
      localStorage.setItem('texhub_referral_code', refFromUrl.toUpperCase());
    } else if (refFromStorage) {
      setReferralCode(refFromStorage);
    }
  }, [searchParams]);

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      alert(isUSA ? 'Please enter your name' : 'Por favor ingresa tu nombre');
      return false;
    }
    if (!formData.email.trim()) {
      alert(isUSA ? 'Please enter your email' : 'Por favor ingresa tu email');
      return false;
    }
    if (formData.password.length < 6) {
      alert(isUSA ? 'Password must be at least 6 characters' : 'La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name.trim()) {
      alert(isUSA ? 'Please enter your company name' : 'Por favor ingresa el nombre de tu empresa');
      return false;
    }
    if (!formData.is_proveedor && !formData.is_maquilero && !formData.is_comprador) {
      alert(isUSA ? 'Please select at least one business category' : 'Por favor selecciona al menos una categoría de negocio');
      return false;
    }
    // Check subcategories
    if (formData.is_proveedor && !formData.proveedor_telas && !formData.proveedor_avios && 
        !formData.proveedor_etiquetas && !formData.proveedor_hilos && !formData.proveedor_empaque && !formData.proveedor_otros) {
      alert(isUSA ? 'Please select what you supply' : 'Por favor selecciona qué provees');
      return false;
    }
    if (formData.is_maquilero && !formData.maquilero_corte && !formData.maquilero_confeccion && 
        !formData.maquilero_acabados && !formData.maquilero_bordado && !formData.maquilero_estampado && 
        !formData.maquilero_tenido && !formData.maquilero_otros) {
      alert(isUSA ? 'Please select your services' : 'Por favor selecciona tus servicios');
      return false;
    }
    if (formData.is_comprador && !formData.comprador_fabricante && !formData.comprador_revendedor && 
        !formData.comprador_tienda && !formData.comprador_marca && !formData.comprador_exportador && !formData.comprador_otros) {
      alert(isUSA ? 'Please select your buyer type' : 'Por favor selecciona tu tipo de comprador');
      return false;
    }
    if (!acceptedTerms) {
      alert(isUSA ? 'You must accept the terms and conditions' : 'Debes aceptar los términos y condiciones');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    setLoading(true);

    try {
      const userData = await register(
        formData.name, 
        formData.email, 
        formData.password, 
        referralCode,
        // Company data
        {
          company_name: formData.company_name,
          phone: formData.phone,
          location_state: formData.location_state,
          location_city: formData.location_city,
          is_proveedor: formData.is_proveedor,
          is_maquilero: formData.is_maquilero,
          is_comprador: formData.is_comprador,
          proveedor_telas: formData.proveedor_telas,
          proveedor_avios: formData.proveedor_avios,
          proveedor_etiquetas: formData.proveedor_etiquetas,
          proveedor_hilos: formData.proveedor_hilos,
          proveedor_empaque: formData.proveedor_empaque,
          proveedor_otros: formData.proveedor_otros,
          maquilero_corte: formData.maquilero_corte,
          maquilero_confeccion: formData.maquilero_confeccion,
          maquilero_acabados: formData.maquilero_acabados,
          maquilero_bordado: formData.maquilero_bordado,
          maquilero_estampado: formData.maquilero_estampado,
          maquilero_tenido: formData.maquilero_tenido,
          maquilero_otros: formData.maquilero_otros,
          comprador_fabricante: formData.comprador_fabricante,
          comprador_revendedor: formData.comprador_revendedor,
          comprador_tienda: formData.comprador_tienda,
          comprador_marca: formData.comprador_marca,
          comprador_exportador: formData.comprador_exportador,
          comprador_otros: formData.comprador_otros,
        }
      );
      
      localStorage.removeItem('texhub_referral_code');
      
      // Go directly to dashboard since company is already created
      window.location.href = '/dashboard';
    } catch (err) {
      const msg = (err && typeof err.message === 'string') ? err.message : (isUSA ? 'Error creating account' : 'Error al crear la cuenta');
      alert(msg);
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!acceptedTerms) {
      alert(isUSA ? 'You must accept the terms and conditions' : 'Debes aceptar los términos y condiciones');
      return;
    }
    setGoogleLoading(true);
    loginWithGoogle();
  };

  const benefits = isUSA ? [
    "Access to 500+ verified suppliers",
    "Request quotes from multiple suppliers at once",
    "Request samples with one click",
    "No commitments, cancel anytime"
  ] : [
    "Acceso a +500 proveedores verificados",
    "Cotiza de múltiples proveedores a la vez",
    "Solicita muestras con un clic",
    "Sin compromisos, cancela cuando quieras"
  ];

  // Category checkbox component
  const CategoryCheckbox = ({ id, checked, onChange, label, description }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors">
      <Checkbox 
        id={id} 
        checked={checked}
        onCheckedChange={onChange}
        className="mt-0.5"
      />
      <label htmlFor={id} className="cursor-pointer flex-1">
        <span className="font-medium text-slate-800 block">{label}</span>
        {description && <span className="text-xs text-slate-500">{description}</span>}
      </label>
    </div>
  );

  // Subcategory checkbox
  const SubCheckbox = ({ id, checked, onChange, label }) => (
    <div className="flex items-center gap-2">
      <Checkbox 
        id={id} 
        checked={checked}
        onCheckedChange={onChange}
        className="h-4 w-4"
      />
      <label htmlFor={id} className="text-sm text-slate-700 cursor-pointer">{label}</label>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-center" style={{ backgroundColor: '#1E4C8E' }}>
        <div className="max-w-md">
          <div className="mb-8">
            <TexHubLogo size="large" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA 
              ? 'Join the largest B2B textile marketplace' 
              : 'Únete a la comunidad textil más grande de México'}
          </h1>
          
          <p className="text-blue-200 mb-8">
            {isUSA 
              ? 'Thousands of companies are already saving time and money with TexHub'
              : 'Miles de empresas ya están ahorrando tiempo y dinero con TexHub'}
          </p>
          
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-blue-100">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#34B772' }} />
                {benefit}
              </li>
            ))}
          </ul>

          <div className="mt-12 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <p className="font-medium" style={{ color: '#34B772' }}>
              {isUSA 
                ? '"I found 3 new suppliers in my first week"'
                : '"Encontré 3 nuevos proveedores en mi primera semana"'}
            </p>
            <p className="text-sm text-blue-200 mt-2">
              {isUSA ? '— Mary G., Northern Apparel' : '— María G., Confecciones del Norte'}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200">
          <div className="px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/">
                <TexHubLogo size="default" />
              </Link>
              <RegionSelector variant="minimal" showLabel={false} />
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4 lg:p-8 overflow-y-auto">
          <div className="w-full max-w-md">
            {/* Launch Promotion Banner */}
            <LaunchPromoBanner variant="compact" className="mb-6" />
            
            {/* Referral Code Banner */}
            {referralCode && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <p className="text-sm text-emerald-800">
                  {isUSA 
                    ? `You're registering with referral code: ${referralCode}`
                    : `Te registras con código de referido: ${referralCode}`}
                </p>
              </div>
            )}
            
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 1 ? 'bg-blue-600 text-white' : 'bg-emerald-500 text-white'}`}>
                {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
              </div>
              <div className="w-12 h-0.5 bg-slate-200">
                <div className={`h-full bg-emerald-500 transition-all ${step > 1 ? 'w-full' : 'w-0'}`}></div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                2
              </div>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
                {step === 1 
                  ? (isUSA ? 'Create your account' : 'Crea tu cuenta')
                  : (isUSA ? 'Your company info' : 'Datos de tu empresa')
                }
              </h2>
              <p className="text-slate-600 mt-2">
                {step === 1 
                  ? (isUSA ? 'Step 1 of 2 - Personal info' : 'Paso 1 de 2 - Datos personales')
                  : (isUSA ? 'Step 2 of 2 - Business info' : 'Paso 2 de 2 - Datos del negocio')
                }
              </p>
            </div>

            {step === 1 ? (
              /* STEP 1: Personal Data */
              <div className="space-y-4">
                {/* Google Button First */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 mb-2"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  data-testid="google-register-btn"
                >
                  {googleLoading ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                  )}
                  {isUSA ? 'Continue with Google' : 'Continuar con Google'}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-50 px-2 text-slate-500">{isUSA ? 'Or with email' : 'O con correo'}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">{isUSA ? 'Your name' : 'Tu nombre'} *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder={isUSA ? 'What is your name?' : '¿Cómo te llamas?'}
                    className="h-12"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{isUSA ? 'Email' : 'Correo electrónico'} *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={isUSA ? 'you@company.com' : 'tu@empresa.com'}
                    className="h-12"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{isUSA ? 'Password' : 'Contraseña'} *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={isUSA ? 'At least 6 characters' : 'Mínimo 6 caracteres'}
                    className="h-12"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="register-password-input"
                  />
                </div>

                <Button 
                  type="button" 
                  className="w-full h-12 text-base font-semibold hover:opacity-90"
                  style={{ backgroundColor: '#1E4C8E' }}
                  onClick={handleNextStep}
                  data-testid="register-next-btn"
                >
                  {isUSA ? 'Continue' : 'Continuar'}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            ) : (
              /* STEP 2: Company Data */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">{isUSA ? 'Company name' : 'Nombre de la empresa'} *</Label>
                  <Input
                    id="company_name"
                    type="text"
                    placeholder={isUSA ? 'Your company name' : 'Nombre de tu empresa'}
                    className="h-12"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    data-testid="register-company-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="phone">{isUSA ? 'Phone' : 'Teléfono'}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="55 1234 5678"
                      className="h-10"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="register-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location_city">{isUSA ? 'City' : 'Ciudad'}</Label>
                    <Input
                      id="location_city"
                      type="text"
                      placeholder={isUSA ? 'City' : 'Ciudad'}
                      className="h-10"
                      value={formData.location_city}
                      onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                      data-testid="register-city-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location_state">{isUSA ? 'State' : 'Estado'}</Label>
                  <Select value={formData.location_state} onValueChange={(v) => setFormData({ ...formData, location_state: v })}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder={isUSA ? 'Select state' : 'Selecciona estado'} />
                    </SelectTrigger>
                    <SelectContent>
                      {MEXICAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Business Categories */}
                <div className="space-y-3 pt-2">
                  <Label className="text-base font-semibold">{isUSA ? 'What does your company do?' : '¿Qué hace tu empresa?'} *</Label>
                  <p className="text-xs text-slate-500">{isUSA ? 'Select all that apply' : 'Selecciona todas las que apliquen'}</p>
                  
                  <div className="space-y-2">
                    <CategoryCheckbox
                      id="is_proveedor"
                      checked={formData.is_proveedor}
                      onChange={(checked) => setFormData({ ...formData, is_proveedor: checked })}
                      label={isUSA ? "Supplier" : "Proveedor de materiales"}
                      description={isUSA ? "Sell fabrics, threads, trims, labels..." : "Vendo telas, hilos, avíos, etiquetas..."}
                    />
                    
                    {/* Proveedor subcategories */}
                    {formData.is_proveedor && (
                      <div className="ml-6 p-3 bg-slate-50 rounded-lg grid grid-cols-2 gap-2">
                        <SubCheckbox id="proveedor_telas" checked={formData.proveedor_telas} 
                          onChange={(c) => setFormData({...formData, proveedor_telas: c})} label={isUSA ? "Fabrics" : "Telas"} />
                        <SubCheckbox id="proveedor_avios" checked={formData.proveedor_avios} 
                          onChange={(c) => setFormData({...formData, proveedor_avios: c})} label={isUSA ? "Trims" : "Avíos"} />
                        <SubCheckbox id="proveedor_etiquetas" checked={formData.proveedor_etiquetas} 
                          onChange={(c) => setFormData({...formData, proveedor_etiquetas: c})} label={isUSA ? "Labels" : "Etiquetas"} />
                        <SubCheckbox id="proveedor_hilos" checked={formData.proveedor_hilos} 
                          onChange={(c) => setFormData({...formData, proveedor_hilos: c})} label={isUSA ? "Threads" : "Hilos"} />
                        <SubCheckbox id="proveedor_empaque" checked={formData.proveedor_empaque} 
                          onChange={(c) => setFormData({...formData, proveedor_empaque: c})} label={isUSA ? "Packaging" : "Empaque"} />
                        <SubCheckbox id="proveedor_otros" checked={formData.proveedor_otros} 
                          onChange={(c) => setFormData({...formData, proveedor_otros: c})} label={isUSA ? "Other" : "Otros"} />
                      </div>
                    )}

                    <CategoryCheckbox
                      id="is_maquilero"
                      checked={formData.is_maquilero}
                      onChange={(checked) => setFormData({ ...formData, is_maquilero: checked })}
                      label={isUSA ? "Manufacturing services" : "Maquilador / Procesos"}
                      description={isUSA ? "Cutting, sewing, printing, embroidery..." : "Corte, confección, estampado, bordado..."}
                    />
                    
                    {/* Maquilador subcategories */}
                    {formData.is_maquilero && (
                      <div className="ml-6 p-3 bg-slate-50 rounded-lg grid grid-cols-2 gap-2">
                        <SubCheckbox id="maquilero_corte" checked={formData.maquilero_corte} 
                          onChange={(c) => setFormData({...formData, maquilero_corte: c})} label={isUSA ? "Cutting" : "Corte"} />
                        <SubCheckbox id="maquilero_confeccion" checked={formData.maquilero_confeccion} 
                          onChange={(c) => setFormData({...formData, maquilero_confeccion: c})} label={isUSA ? "Sewing" : "Confección"} />
                        <SubCheckbox id="maquilero_acabados" checked={formData.maquilero_acabados} 
                          onChange={(c) => setFormData({...formData, maquilero_acabados: c})} label={isUSA ? "Finishing" : "Acabados"} />
                        <SubCheckbox id="maquilero_bordado" checked={formData.maquilero_bordado} 
                          onChange={(c) => setFormData({...formData, maquilero_bordado: c})} label={isUSA ? "Embroidery" : "Bordado"} />
                        <SubCheckbox id="maquilero_estampado" checked={formData.maquilero_estampado} 
                          onChange={(c) => setFormData({...formData, maquilero_estampado: c})} label={isUSA ? "Printing" : "Estampado"} />
                        <SubCheckbox id="maquilero_tenido" checked={formData.maquilero_tenido} 
                          onChange={(c) => setFormData({...formData, maquilero_tenido: c})} label={isUSA ? "Dyeing" : "Teñido"} />
                        <SubCheckbox id="maquilero_otros" checked={formData.maquilero_otros} 
                          onChange={(c) => setFormData({...formData, maquilero_otros: c})} label={isUSA ? "Other" : "Otros"} />
                      </div>
                    )}

                    <CategoryCheckbox
                      id="is_comprador"
                      checked={formData.is_comprador}
                      onChange={(checked) => setFormData({ ...formData, is_comprador: checked })}
                      label={isUSA ? "Buyer" : "Comprador"}
                      description={isUSA ? "Manufacturer, store, brand, reseller..." : "Fabricante, tienda, marca, revendedor..."}
                    />
                    
                    {/* Comprador subcategories */}
                    {formData.is_comprador && (
                      <div className="ml-6 p-3 bg-slate-50 rounded-lg grid grid-cols-2 gap-2">
                        <SubCheckbox id="comprador_fabricante" checked={formData.comprador_fabricante} 
                          onChange={(c) => setFormData({...formData, comprador_fabricante: c})} label={isUSA ? "Manufacturer" : "Fabricante"} />
                        <SubCheckbox id="comprador_tienda" checked={formData.comprador_tienda} 
                          onChange={(c) => setFormData({...formData, comprador_tienda: c})} label={isUSA ? "Store" : "Tienda"} />
                        <SubCheckbox id="comprador_marca" checked={formData.comprador_marca} 
                          onChange={(c) => setFormData({...formData, comprador_marca: c})} label={isUSA ? "Brand" : "Marca"} />
                        <SubCheckbox id="comprador_revendedor" checked={formData.comprador_revendedor} 
                          onChange={(c) => setFormData({...formData, comprador_revendedor: c})} label={isUSA ? "Reseller" : "Revendedor"} />
                        <SubCheckbox id="comprador_exportador" checked={formData.comprador_exportador} 
                          onChange={(c) => setFormData({...formData, comprador_exportador: c})} label={isUSA ? "Exporter" : "Exportador"} />
                        <SubCheckbox id="comprador_otros" checked={formData.comprador_otros} 
                          onChange={(c) => setFormData({...formData, comprador_otros: c})} label={isUSA ? "Other" : "Otros"} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="flex items-start gap-3 py-2">
                  <Checkbox 
                    id="terms" 
                    checked={acceptedTerms}
                    onCheckedChange={setAcceptedTerms}
                    data-testid="accept-terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-600 leading-tight cursor-pointer">
                    {isUSA ? 'I accept the ' : 'Acepto los '}
                    <Link to="/terms" className="text-blue-600 hover:underline" target="_blank">
                      {isUSA ? 'Terms and Conditions' : 'Términos y Condiciones'}
                    </Link>{' '}
                    {isUSA ? 'and the ' : 'y el '}
                    <Link to="/privacy" className="text-blue-600 hover:underline" target="_blank">
                      {isUSA ? 'Privacy Policy' : 'Aviso de Privacidad'}
                    </Link>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    className="h-12"
                    onClick={() => setStep(1)}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-12 text-base font-semibold hover:opacity-90"
                    style={{ backgroundColor: '#34B772' }}
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {isUSA ? 'Creating...' : 'Creando...'}
                      </>
                    ) : (
                      <>
                        {isUSA ? 'Create my account' : 'Crear mi cuenta'}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            <p className="text-center text-sm text-slate-600 mt-6">
              {isUSA ? 'Already have an account? ' : '¿Ya tienes cuenta? '}
              <Link to="/login" className="font-medium hover:underline" style={{ color: '#1E4C8E' }}>
                {isUSA ? 'Log in here' : 'Entra aquí'}
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Register;
